import { Flex, Divider, HStack, Box, Text, DataList, DataItem } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useEsriMapStore } from '../../../hooks/use-esri-map-store';
import { useBootstrapState } from '../../../../bootstrap/bootstrap-provider';
import { getSubdivisionItemsBySingleAgencyType } from '../helpers';
import { AgencyTypeCapitalized } from '../../../../common/types';
import { useApplicationType } from '../../../../common/hooks/use-application-type';
import { getOneSizeSmaller } from '../../../../common/helpers/utils';
import { getDataItemText } from '../../../../common/helpers/component-utils';

export const LocationInfoSingleAgencySection = observer(
    function LocationInfoSingleAgencySection(): React.ReactElement {
        const mapStore = useEsriMapStore();
        const { subdivisionOptionsBootstrapResponse } = useBootstrapState();

        const { isFirstResponder } = useApplicationType();
        const size = isFirstResponder ? 'lg' : 'md';
        const smallerSize = getOneSizeSmaller(size);

        const geoPointLocation = mapStore.locationSearchSelectedGeoPointLocation;
        const entityLinks = geoPointLocation?.entityLinks?.[0];

        const combinedSubdivisionOptionsBootstrapView =
            subdivisionOptionsBootstrapResponse?.combinedSubdivisionOptionsBootstrapView;

        const { policeDispatchArea, fireDispatchArea, medicalDispatchArea } = entityLinks || {};

        const policeSubdivisions = getSubdivisionItemsBySingleAgencyType(
            AgencyTypeCapitalized.POLICE,
            entityLinks,
            combinedSubdivisionOptionsBootstrapView
        );
        const fireSubdivisions = getSubdivisionItemsBySingleAgencyType(
            AgencyTypeCapitalized.FIRE,
            entityLinks,
            combinedSubdivisionOptionsBootstrapView
        );
        const emsSubdivisions = getSubdivisionItemsBySingleAgencyType(
            AgencyTypeCapitalized.EMS,
            entityLinks,
            combinedSubdivisionOptionsBootstrapView
        );

        return (
            <Box data-testid="MAP_LOCATION_PIN_INFO_SINGLE_AGENCY_SECTION">
                <Flex direction="column" padding="12px 18px">
                    <Box marginBottom={3}>
                        <Text fontSize={size} fontWeight="bold">
                            Location Info
                        </Text>
                    </Box>
                    <DataList direction="column" variant="stacked" spacing={3}>
                        <HStack alignItems="flex-start" gap={4}>
                            <DataItem
                                dataKey={<Text fontSize={smallerSize}>Police Subd.</Text>}
                                style={{
                                    flexBasis: '50%',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {getDataItemText(size, policeSubdivisions)}
                            </DataItem>
                            <DataItem
                                dataKey={<Text fontSize={smallerSize}>Police D-Area</Text>}
                                style={{ flexBasis: '50%', wordBreak: 'break-word' }}
                            >
                                {getDataItemText(size, policeDispatchArea)}
                            </DataItem>
                        </HStack>
                        <HStack alignItems="flex-start" gap={4}>
                            <DataItem
                                dataKey={<Text fontSize={smallerSize}>Fire Subd.</Text>}
                                style={{ flexBasis: '50%', wordBreak: 'break-word' }}
                            >
                                {getDataItemText(size, fireSubdivisions)}
                            </DataItem>
                            <DataItem
                                dataKey={<Text fontSize={smallerSize}>Fire D-Area</Text>}
                                style={{ flexBasis: '50%', wordBreak: 'break-word' }}
                            >
                                {getDataItemText(size, fireDispatchArea)}
                            </DataItem>
                        </HStack>
                        <HStack alignItems="flex-start" gap={4}>
                            <DataItem
                                dataKey={<Text fontSize={smallerSize}>EMS Subd.</Text>}
                                style={{ flexBasis: '50%', wordBreak: 'break-word' }}
                            >
                                {getDataItemText(size, emsSubdivisions)}
                            </DataItem>
                            <DataItem
                                dataKey={<Text fontSize={smallerSize}>EMS D-Area</Text>}
                                style={{ flexBasis: '50%', wordBreak: 'break-word' }}
                            >
                                {getDataItemText(size, medicalDispatchArea)}
                            </DataItem>
                        </HStack>
                    </DataList>
                </Flex>
                <Divider />
            </Box>
        );
    }
);
